import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Menu, X, LogOut, LayoutDashboard, ArrowUpRight, Mail } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/aprende", label: "Aprende Ciencia" },
];

export default function Layout({ children }) {
  const { user, isAdmin, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  const isActive = (to) => loc.pathname === to || loc.pathname.startsWith(to + "/");

  return (
    <div className="min-h-screen flex flex-col bg-white text-ink">
      <header className="absolute top-0 inset-x-0 z-50 bg-transparent pb-4">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 h-[70px] flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group" data-testid="brand-logo">
            <span className="relative flex h-16 w-16 items-center justify-center shrink-0">
              <img src="/einstein-logo.png" alt="Albert Einstein" className="h-full w-full object-contain group-hover:scale-110 transition-transform duration-300" />
            </span>
            <div className="leading-none">
              <div className="font-display font-extrabold text-[17px] tracking-tight text-white">El Portal de La Ciencia</div>
              <div className="font-hand text-white text-[17px] leading-none mt-0.5">Del Big Bang a la Relatividad General y más</div>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                className={`px-4 py-2 text-lg lg:text-xl font-bold transition-colors border-b-2 -mb-[2px] ${
                  isActive(n.to) ? "text-white border-white" : "text-white/80 border-transparent hover:text-white hover:border-white/40"
                }`}
              >
                {n.label}
              </Link>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-2">
            {isAdmin ? (
              <>
                <button onClick={() => nav("/admin")} data-testid="nav-admin" className="inline-flex items-center gap-1.5 text-sm font-semibold text-white/80 hover:text-white px-3 py-2">
                  <LayoutDashboard size={16} /> Panel
                </button>
                <button onClick={async () => { await logout(); nav("/"); }} data-testid="nav-logout" className="inline-flex items-center gap-1.5 text-sm font-semibold text-white/80 hover:text-white px-3 py-2">
                  <LogOut size={16} /> Salir
                </button>
              </>
            ) : (
              user === false && (
                <button onClick={() => nav("/login")} data-testid="nav-login" className="inline-flex items-center gap-1.5 bg-white/10 border border-white/40 text-white text-sm font-semibold px-5 py-2.5 rounded-full hover:bg-white/20 transition-colors backdrop-blur-sm">
                  Acceso Admin <ArrowUpRight size={15} />
                </button>
              )
            )}
          </div>

          <button className="md:hidden text-white" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
            {open ? <X /> : <Menu />}
          </button>
        </div>

        {open && (
          <div className="md:hidden border-t border-paper-200 bg-white px-5 py-4 flex flex-col gap-1">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} onClick={() => setOpen(false)} className={`px-3 py-2.5 text-sm font-semibold ${isActive(n.to) ? "text-nasa-blue" : "text-ink-700"}`}>
                {n.label}
              </Link>
            ))}
            {isAdmin ? (
              <>
                <Link to="/admin" onClick={() => setOpen(false)} className="px-3 py-2.5 text-sm font-semibold text-ink-700">Panel Admin</Link>
                <button onClick={async () => { await logout(); setOpen(false); nav("/"); }} className="px-3 py-2.5 text-sm text-left font-semibold text-ink-900">Cerrar sesión</button>
              </>
            ) : (
              <Link to="/login" onClick={() => setOpen(false)} className="px-3 py-2.5 text-sm font-semibold text-nasa-blue">Acceso Admin</Link>
            )}
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <footer className="bg-nasa-navy text-white mt-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-14">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 pb-8 border-b border-white/15">
            <div className="flex items-center gap-3">
              <span className="flex h-14 w-14 items-center justify-center shrink-0"><img src="/einstein-logo.png" alt="Albert Einstein" className="h-full w-full object-contain" /></span>
              <div>
                <div className="font-display font-extrabold text-xl">El Portal de La Ciencia</div>
                <div className="eyebrow text-[10px] text-white/60 mt-1">PLATAFORMA DE DIVULGACIÓN CIENTÍFICA</div>
              </div>
            </div>
            <div className="bg-white/5 border border-white/15 rounded-lg p-5 w-full md:w-auto md:min-w-[320px]">
              <div className="flex items-center gap-2 eyebrow text-[10px] text-white/60 mb-2"><Mail size={14} /> CONTACTO</div>
              <p className="text-sm text-white/80 mb-3">¿Dudas o sugerencias? Escríbeme un correo:</p>
              <a
                href="mailto:edwincamachoperez2003@gmail.com"
                data-testid="footer-contact-email"
                className="inline-flex items-center gap-2 bg-white text-nasa-navy text-sm font-semibold px-5 py-2.5 rounded-full hover:bg-white/90 transition-colors"
              >
                <Mail size={15} /> Escríbeme un correo
              </a>
            </div>
          </div>
          <p className="eyebrow text-[10px] text-white/40 mt-6">© {new Date().getFullYear()} EL PORTAL DE LA CIENCIA</p>
        </div>
      </footer>
    </div>
  );
}
